import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {assert} from 'Asserts';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ProcedureValidationErrorInfoButton from 'procedures/design/buttons/ProcedureValidationErrorInfoButton';
import {
  getMethodNameOrPlaceholder,
  makePlaceholderNameClass,
} from 'procedures/design/procedureMethodNodeCommonConsts';
import {ProcedureValidationObjectType} from 'procedures/design/validation/ProcedureValidationTypes';
import {Method} from 'procedures/ProcedureDomainTypes';
import getProcedureUIString, {ProceduresStringKeys} from 'procedures/ProceduresStringKeys';
import {multiLineEllipsisText} from 'styles/Utilities';
import {BLUE_POWDER_LIGHT, GRAY, GRAY_DARKER, GRAY_LIGHTER} from 'ui-library/Colors';
import {Header3} from 'ui-library/Headers';

interface MethodSelectorSidebarProps<TId extends 'id' | 'cid'> {
  methods: Array<Pick<Method, 'name' | TId>>;
  methodIdKey: TId; // Use cid for designer, id for any planning context
  selectedMethodId: string | null;
  onChangeSelection: (newMethodId: string) => void;
  sidebarWidth: number;
  procedureType: ProcedureType;
}

interface OneMethodListingProps {
  method: Pick<Method, 'name'>;
  methodId: string;
  isSelected: boolean;
  onClick: (methodId: string) => void;
  procedureType: ProcedureType;
}

export function OneMethodListing(props: OneMethodListingProps): JSX.Element {
  return (
    <div
      onClick={() => {
        props.onClick(props.methodId);
      }}
      className={css(styles.methodNameWrapper, props.isSelected && styles.selectedMethodWrapper)}
      {...getTestProps({
        component: 'MethodSelectorSidebar',
        element: 'methodListing',
        key: props.methodId,
      })}
      data-amp-safe={false}
    >
      <ProcedureValidationErrorInfoButton
        primaryObject={{id: props.methodId, objectType: ProcedureValidationObjectType.METHOD}}
      />
      <p
        className={classNames(
          css(styles.methodNameTextEllipses, props.isSelected && styles.selectedMethod),
          makePlaceholderNameClass(props.method.name)
        )}
      >
        {getMethodNameOrPlaceholder(props.method.name, props.procedureType)}
      </p>
    </div>
  );
}

function extractMethodIdByKey<TId extends 'id' | 'cid'>(method: Pick<Method, TId>, methodIdKey: TId): string {
  const extractedId = method[methodIdKey];
  assert(extractedId != null, 'Method IDs must not be null at this stage');
  return extractedId;
}

/**
 * Sidebar to enable method navigation. Supports method selection via id or cid depending on the
 * context the methods are being used. Use cid for the procedure designer, and id for any planning context.
 */
export default function MethodSelectorSidebar<TId extends 'id' | 'cid'>(
  props: MethodSelectorSidebarProps<TId>
): JSX.Element {
  const handleClickMethodName = (methodId: string): void => {
    props.onChangeSelection(methodId);
  };

  return (
    <div style={{width: `${props.sidebarWidth}px`}}>
      <Header3 className={css(styles.heading)}>
        {`${getProcedureUIString(ProceduresStringKeys.METHOD, props.procedureType, {plural: true})}`}
      </Header3>
      {props.methods.map((m) => {
        const methodId = extractMethodIdByKey(m, props.methodIdKey);
        return (
          <OneMethodListing
            key={methodId}
            method={m}
            methodId={methodId}
            isSelected={methodId === props.selectedMethodId}
            onClick={handleClickMethodName}
            procedureType={props.procedureType}
          />
        );
      })}
    </div>
  );
}

const borderStyle = `1px solid ${GRAY}`;

const styles = StyleSheet.create({
  heading: {
    lineHeight: '16px',
    borderRight: borderStyle,
    borderBottom: borderStyle,
    padding: '12px 12px',
    margin: 0,
    background: GRAY_LIGHTER,
    fontSize: '13px',
  },
  methodNameWrapper: {
    lineHeight: '14px',
    height: '46px',
    fontWeight: 400,
    borderRight: borderStyle,
    borderBottom: borderStyle,
    padding: '12px 12px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    ':hover': {
      background: GRAY_LIGHTER,
    },
  },
  selectedMethodWrapper: {
    background: BLUE_POWDER_LIGHT,
    ':hover': {
      background: BLUE_POWDER_LIGHT,
    },
  },
  methodNameTextEllipses: {
    fontSize: '13px',
    lineHeight: 1.5,
    verticalAlign: 'middle',
    margin: 0,
    paddingRight: '2px',
    textAlign: 'left',
    ...multiLineEllipsisText(2),
  },
  placeholderName: {
    fontStyle: 'italic',
    color: GRAY_DARKER,
  },
  selectedMethod: {
    fontWeight: 'bold',
  },
});
